import { combineEpics, Epic, ofType } from "redux-observable";
import { EMPTY, from, Observable } from "rxjs";
import { map, switchMap, withLatestFrom } from "rxjs/operators";
import { AppStore } from "..";
import { getOption } from "../../utils/utils";
import { AuthActions } from "../auth/actions";
import {
  FetchableError,
  FetchableSuccess,
  isFetchableSuccess,
} from "../common/fetchable/helpers";
import { VerificationActions } from "./actions";
import { VerificationDTO as AMLVerificationDTO } from "./models/dto/aml";
import { VerificationDTO as DueDiligenceVerificationDTO } from "./models/dto/due-diligence";
import { VerificationDTO as KYCVerificationDTO } from "./models/dto/kyc";
import { VerificationService } from "./service";

// --- KYC/KYB ----
const getAllSelfKycEpic: Epic<VerificationActions.Actions> = (action$) =>
  action$.pipe(
    ofType(VerificationActions.ActionTypes.getAllSelfKyc),
    switchMap<
      VerificationActions.Actions,
      Observable<
        | FetchableError
        | FetchableSuccess<KYCVerificationDTO.Responses.GetAllKyc.T>
      >
    >(() => VerificationService.getAllKyc({}, true)),
    map((x) => VerificationActions.setAllSelfKyc(x))
  );

const setAllSelfKycPaginationEpic: Epic<VerificationActions.Actions> = (
  action$
) =>
  action$.pipe(
    ofType(VerificationActions.ActionTypes.setAllSelfKycPagination),
    switchMap<
      VerificationActions.SetAllSelfKycPagination,
      Observable<
        | FetchableError
        | FetchableSuccess<KYCVerificationDTO.Responses.GetAllKyc.T>
      >
    >((action) => VerificationService.getAllKyc(action.payload, true)),
    map((x) => VerificationActions.setAllSelfKyc(x))
  );

const getAllKycEpic: Epic<VerificationActions.Actions> = (action$, state$) =>
  action$.pipe(
    ofType(VerificationActions.ActionTypes.getAllKyc),
    withLatestFrom(state$),
    switchMap<
      [VerificationActions.Actions, AppStore],
      Observable<
        | FetchableError
        | FetchableSuccess<KYCVerificationDTO.Responses.GetAllKyc.T>
      >
    >(([_, store]) =>
      VerificationService.getAllKyc(
        undefined,
        isFetchableSuccess(store.auth.login) &&
          store.auth.login.data.accessToken.roles.includes("ROLE_ADMIN")
          ? undefined
          : false
      )
    ),
    map((x) => VerificationActions.setAllKyc(x))
  );

const setAllKycPaginationEpic: Epic<VerificationActions.Actions> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType<
      VerificationActions.Actions,
      VerificationActions.SetAllKycPagination
    >(VerificationActions.ActionTypes.setAllKycPagination),
    withLatestFrom(state$),
    switchMap<
      [VerificationActions.SetAllKycPagination, AppStore],
      Observable<
        | FetchableError
        | FetchableSuccess<KYCVerificationDTO.Responses.GetAllKyc.T>
      >
    >(([action, store]) =>
      VerificationService.getAllKyc(
        action.payload,
        isFetchableSuccess(store.auth.login) &&
          store.auth.login.data.accessToken.roles.includes("ROLE_ADMIN")
          ? undefined
          : false
      )
    ),
    map((x) => VerificationActions.setAllKyc(x))
  );

const getAllKybEpic: Epic<VerificationActions.Actions> = (action$) =>
  action$.pipe(
    ofType(VerificationActions.ActionTypes.getAllKyb),
    switchMap<
      VerificationActions.Actions,
      Observable<
        | FetchableError
        | FetchableSuccess<KYCVerificationDTO.Responses.GetAllKyb.T>
      >
    >(() => VerificationService.getAllKyb()),
    map((x) => VerificationActions.setAllKyb(x))
  );

const setAllKybPaginationEpic: Epic<VerificationActions.Actions> = (action$) =>
  action$.pipe(
    ofType(VerificationActions.ActionTypes.setAllKybPagination),
    switchMap<
      VerificationActions.SetAllKybPagination,
      Observable<
        | FetchableError
        | FetchableSuccess<KYCVerificationDTO.Responses.GetAllKyb.T>
      >
    >((action) => VerificationService.getAllKyb(action.payload)),
    map((x) => VerificationActions.setAllKyb(x))
  );

const getChecksSummaryEpic: Epic<VerificationActions.Actions> = (action$) =>
  action$.pipe(
    ofType(VerificationActions.ActionTypes.getChecksSummary),
    switchMap<
      VerificationActions.Actions,
      Observable<
        | FetchableError
        | FetchableSuccess<KYCVerificationDTO.Responses.GetChecksSummary.T>
      >
    >(() => VerificationService.getChecksSummary()),
    map((x) => VerificationActions.setChecksSummary(x))
  );

// --- AML ---
const getAllIndividualAmlEpic: Epic<VerificationActions.Actions> = (action$) =>
  action$.pipe(
    ofType(VerificationActions.ActionTypes.getAllIndividualAml),
    switchMap<
      VerificationActions.Actions,
      Observable<
        | FetchableError
        | FetchableSuccess<AMLVerificationDTO.Responses.GetAllIndividualAml.T>
      >
    >(() => VerificationService.getAllIndividualAml()),
    map((x) => VerificationActions.setAllIndividualAml(x))
  );

const setAllIndividualAmlPaginationEpic: Epic<VerificationActions.Actions> = (
  action$
) =>
  action$.pipe(
    ofType(VerificationActions.ActionTypes.setAllIndividualAmlPagination),
    switchMap<
      VerificationActions.SetAllIndividualAmlPagination,
      Observable<
        | FetchableError
        | FetchableSuccess<AMLVerificationDTO.Responses.GetAllIndividualAml.T>
      >
    >((action) => VerificationService.getAllIndividualAml(action.payload)),
    map((x) => VerificationActions.setAllIndividualAml(x))
  );

const getAllBusinessAmlEpic: Epic<VerificationActions.Actions> = (action$) =>
  action$.pipe(
    ofType(VerificationActions.ActionTypes.getAllBusinessAml),
    switchMap<
      VerificationActions.Actions,
      Observable<
        | FetchableError
        | FetchableSuccess<AMLVerificationDTO.Responses.GetAllBusinessAml.T>
      >
    >(() => VerificationService.getAllBusinessAml()),
    map((x) => VerificationActions.setAllBusinessAml(x))
  );

const setAllBusinessAmlPaginationEpic: Epic<VerificationActions.Actions> = (
  action$
) =>
  action$.pipe(
    ofType(VerificationActions.ActionTypes.setAllBusinessAmlPagination),
    switchMap<
      VerificationActions.SetAllBusinessAmlPagination,
      Observable<
        | FetchableError
        | FetchableSuccess<AMLVerificationDTO.Responses.GetAllBusinessAml.T>
      >
    >((action) => VerificationService.getAllBusinessAml(action.payload)),
    map((x) => VerificationActions.setAllBusinessAml(x))
  );

// const getKYBJurisdictionsEpic: Epic<VerificationActions.Actions> = (action$) =>
//   action$.pipe(
//     ofType(VerificationActions.ActionTypes.getKYBJurisdictions),
//     switchMap<
//       VerificationActions.Actions,
//       Observable<
//         | FetchableError
//         | FetchableSuccess<KYCVerificationDTO.Responses.GetKYBJurisdictions.T>
//       >
//     >(() => VerificationService.getKYBJurisdictions()),
//     map((x) => VerificationActions.setKYBJurisdictions(x))
//   );

// --- Due Diligence ---

const getAllDueDiligenceConfigEpic: Epic<VerificationActions.Actions> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType(VerificationActions.ActionTypes.getAllDueDiligenceConfig),
    withLatestFrom(state$),
    switchMap<
      [VerificationActions.Actions, AppStore],
      Observable<
        | FetchableError
        | FetchableSuccess<DueDiligenceVerificationDTO.Responses.DueDiligence.Config.GetAll.T>
      >
    >(([_, store]) => {
      const { login } = store.auth;
      if (
        isFetchableSuccess(login) &&
        login.data.accessToken.roles.includes("ROLE_MERCHANT")
      ) {
        return VerificationService.getAllDueDiligenceConfigs(
          login.data.accessToken.credentialsId
        );
      }
      return EMPTY;
    }),
    map((x) => VerificationActions.setAllDueDiligenceConfig(x))
  );

const setAllDueDiligenceConfigPaginationEpic: Epic<VerificationActions.Actions> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType<
      VerificationActions.Actions,
      VerificationActions.SetAllDueDiligenceConfigPagination
    >(VerificationActions.ActionTypes.setAllDueDiligenceConfigPagination),
    withLatestFrom(state$),
    switchMap<
      [VerificationActions.SetAllDueDiligenceConfigPagination, AppStore],
      Observable<
        | FetchableError
        | FetchableSuccess<DueDiligenceVerificationDTO.Responses.DueDiligence.Config.GetAll.T>
      >
    >(([action, store]) => {
      const { login } = store.auth;
      if (
        isFetchableSuccess(login) &&
        login.data.accessToken.roles.includes("ROLE_MERCHANT")
      ) {
        return VerificationService.getAllDueDiligenceConfigs(
          login.data.accessToken.credentialsId,
          action.payload
        );
      }
      return EMPTY;
    }),
    map((x) => VerificationActions.setAllDueDiligenceConfig(x))
  );

const getCurrentMerchantEpic: Epic<
  VerificationActions.Actions,
  VerificationActions.Actions,
  AppStore
> = (action$, store$) =>
    action$.pipe(
      ofType(VerificationActions.ActionTypes.getCurrentMerchantConfig),
      switchMap<
        VerificationActions.Actions,
        Promise<
          | FetchableError
          | FetchableSuccess<KYCVerificationDTO.Responses.GetMerchantConfig.T>
        >
      >(() =>
        getOption(store$.value.auth.login)
          .filter((x) => x.accessToken.authenticated)
          .fold(
            () =>
              Promise.resolve<FetchableError>({
                status: "error",
                err: new Error("No user ID"),
              }),
            (token) =>
              VerificationService.getMerchantConfig(
                token.accessToken.credentialsId
              )
                .then<
                  FetchableSuccess<KYCVerificationDTO.Responses.GetMerchantConfig.T>
                >((data) => ({
                  status: "ready",
                  data,
                }))
                .catch<FetchableError>((err) => ({
                  status: "error",
                  err,
                }))
          )
      ),
      map((x) => VerificationActions.setCurrentMerchantConfig(x))
    );

const dependencies: Epic<
  AuthActions.Actions | VerificationActions.Actions,
  VerificationActions.Actions
> = (action$) =>
    action$.pipe(
      ofType(AuthActions.ActionTypes.authDone),
      switchMap(() =>
        from([
          VerificationActions.resetAllKyc(),
          VerificationActions.resetAllKyb(),
          VerificationActions.resetChecksSummary(),
          VerificationActions.resetAllBusinessAml(),
          VerificationActions.resetAllIndividualAml(),
          VerificationActions.resetAllDueDiligenceConfig(),
          VerificationActions.resetCurrentMerchantConfig(),
        ])
      )
    );

export const verificationEpic = combineEpics(
  getAllKycEpic,
  setAllKycPaginationEpic,
  getAllSelfKycEpic,
  setAllSelfKycPaginationEpic,
  getAllKybEpic,
  setAllKybPaginationEpic,
  getChecksSummaryEpic,
  getAllIndividualAmlEpic,
  setAllIndividualAmlPaginationEpic,
  getAllBusinessAmlEpic,
  setAllBusinessAmlPaginationEpic,
  // getKYBJurisdictionsEpic,
  getAllDueDiligenceConfigEpic,
  setAllDueDiligenceConfigPaginationEpic,
  getCurrentMerchantEpic,
  dependencies
);
